import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Icon, Progress, Tooltip } from '@equinor/eds-core-react'
import { external_link } from '@equinor/eds-icons'
import { Sensor } from '@/features/dashboard/models'
import { createEchoViewForSensors } from '@/api/echoApiService'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useWindowWidth } from '@/hooks/useWindowWidth'

const DISABLED_TOOLTIP =
    'Echo can only show sensors from one plant at a time. Use the filter to display only sensors from a single plant in Sensor Overview.'

interface IViewFilteredSensorsInEchoButton {
    filteredSensors: Sensor[]
}

const ViewFilteredSensorsInEchoButton: React.FC<IViewFilteredSensorsInEchoButton> = ({ filteredSensors }) => {
    const [isLoading, setIsLoading] = useState(false)
    const windowWidth = useWindowWidth()
    const distinctPlants = new Set(filteredSensors.map((s) => s.plant).filter(Boolean))
    const disabled = filteredSensors.length === 0 || distinctPlants.size > 1

    const handleClick = async () => {
        if (isLoading) return
        setIsLoading(true)
        try {
            const url = await createEchoViewForSensors(filteredSensors)
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error'
            addSnackBarMessage(`Failed to open Echo view: ${message}`)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Tooltip title={disabled ? DISABLED_TOOLTIP : ''} placement="bottom">
            <ButtonSlot>
                <StyledButton variant="outlined" disabled={disabled} aria-busy={isLoading} onClick={handleClick}>
                    {isLoading ? (
                        <Progress.Dots />
                    ) : (
                        <>
                            {windowWidth > 1024 && windowWidth <= 1440
                                ? 'View in Echo'
                                : 'View filtered sensors in Echo'}
                            <Icon data={external_link} />
                        </>
                    )}
                </StyledButton>
            </ButtonSlot>
        </Tooltip>
    )
}

const ButtonSlot = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    margin-left: 8px;

    @media (max-width: 1024px) {
        display: flex;
        width: 100%;
        margin-left: 0;
        margin-top: 8px;
    }
`

const StyledButton = styled(Button)`
    height: 38px;
    white-space: nowrap;

    @media (max-width: 1024px) {
        width: 100%;
    }
`

export default ViewFilteredSensorsInEchoButton
